import type { Dispatch } from "react";
import { useState, useReducer, useEffect } from "react";

/**
 * A custom React hook to allow functional components to run async methods.
 * This hook returns a loading state variable that will trigger a render of the using Component.
 * The result of the async function is returned in the result variable, but you can ignore this if the async function
 * is updating internal state of a different object ( see RegexDlfEditor reuse calculation for a reference)
 *
 * Unmounted components are handled within the `if(mounted)` branch.
 *
 * @param actionAsync
 * @param initialResult
 *
 * S = Reducer State
 * A = Reducer Action
 */
function useAsyncReducer<S, A>(
  actionAsync: (currentState: S) => Promise<A>,
  reducer: (state: S, action: A) => S,
  initialResult: S,
): [S, () => void, boolean, Dispatch<A>] {
  const [loading, setLoading] = useState(false);
  const [state, dispatch] = useReducer(reducer, initialResult);
  const [fetchFlag, setFetchFlag] = useState(0);

  useEffect(() => {
    if (fetchFlag == 0) {
      // Run only after triggerFetch is called
      return;
    }
    let mounted = true;
    setLoading(true);
    actionAsync(state).then((res) => {
      if (mounted) {
        // Only modify state if component is still mounted
        setLoading(false);
        dispatch(res);
        // setResult(res);
      }
    });
    // Signal that compnoent has been 'cleaned up'
    return function cleanup() {
      mounted = false;
    };
  }, [fetchFlag]);

  function triggerFetch(): void {
    // Set fetchFlag to indirectly trigger the useEffect above
    setFetchFlag(Math.random() + 1); // + 1 to avoid generating 0
  }
  return [state, triggerFetch, loading, dispatch];
}

export { useAsyncReducer };
